import { useEffect, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTrackingContext, TrackingProvider } from '@zapier/toolbox-tracking';
import {
  EventName,
  SectionNameType,
} from '../../observability/tracking/events';
import {
  EventAction,
  InteractionName,
  useAvroTracking,
} from '../../observability/tracking/avro';

export type TagType =
  | 'div'
  | 'section'
  | 'article'
  | 'main'
  | 'aside'
  | 'header'
  | 'footer';

export type Props = {
  as?: TagType;
  children: ReactNode;
  className?: string;
  layerName?: string;
  sectionName: SectionNameType;
};

export function InViewTracking({
  children,
  layerName,
  sectionName,
  as: WrapperElement = 'div',
  className,
}: Props) {
  const { trackEvent } = useTrackingContext();
  const { emitUserInteractionEvent } = useAvroTracking();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      emitUserInteractionEvent({
        event_action: EventAction.in_view,
        interaction_name: InteractionName.component_scrolled_into_view,
        option_selected: WrapperElement,
        custom_tracking_fields: {
          section_name: sectionName,
          ...(layerName && { custom_section_name: layerName }),
        },
      });

      trackEvent(EventName.SCROLLED_INTO_VIEW, {
        component: sectionName,
        ...(layerName && { custom_section_name: layerName }),
      });
    }
  }, [
    inView,
    trackEvent,
    sectionName,
    WrapperElement,
    emitUserInteractionEvent,
    layerName,
  ]);

  return (
    <TrackingProvider metadata={{ event_location: sectionName }}>
      <WrapperElement className={className} ref={ref}>
        {children}
      </WrapperElement>
    </TrackingProvider>
  );
}
